import {
  cancelPaymentIntent,
  createGuidePaymentIntent,
  getGuideContent,
  getGuideDetail,
  getGuidePricePreview,
  getGuidesCatalog,
  getMyPurchasedGuides,
  getPaymentIntentStatus,
  refundGuide,
} from "../repositories/Guides2Repository";
import { isPaymentIntentClientSecret } from "../utils/stripeClient";
import {
  mapGuidePaymentIntent,
  mapGuidePricePreview,
  mapPaymentIntentStatus,
} from "../mappers/Guides2Mapper";

// Guides 2.0: orquesta el repository y devuelve response.data tal cual. El
// backend responde el formato canónico { success, message, data }.
//
// DESVIACIÓN CONSCIENTE de CLAUDE.md §3.1, decidida el 22/09/2026: aquí NO hay
// try/catch/log/rethrow. Un solo fallo imprimía tres líneas idénticas en
// consola —repository, service y pantalla— porque las tres capturaban el mismo
// AxiosError y volvían a registrarlo sin agregar nada. El único log se quedó en
// el repository, que es la capa que ve TODOS los fallos (incluidos los del
// detalle, que `Promise.allSettled` se traga en el hook) y que nombra la
// operación; el error de axios ya trae la URL y el estado. Un `catch` que solo
// vuelve a lanzar no aporta, así que se quitó junto con el log en vez de dejar
// un bloque vacío.

// El backend espera el idioma en mayúsculas ("ES"/"EN"); LangContext lo
// maneja en minúsculas. La conversión vive aquí y no en cada página.
const toLanguageCode = (lang) => (lang === "en" ? "EN" : "ES");

// data = [{ guideId, guideGuid, code, coverImageUrl, price, currencyCode,
//           hasFreeVariant, hasPaidVariant, name, description, categoryId,
//           categoryName, authorUserId, authorNickName }]
// Una fila por GUÍA, no por variante: una guía con las dos modalidades es una
// sola tarjeta.
export const getGuidesCatalogService = async ({ lang, searchText = "", page = 1, pageSize = 12 }) => {
  const response = await getGuidesCatalog({
    languageCode: toLanguageCode(lang),
    searchText,
    page,
    pageSize,
  });
  return response.data;
};

// data = [{ ...datos de la guía, guideVariantId, guideVariantTypeCode,
//           hasAccess, ... }] — una fila por variante activa.
export const getGuideDetailService = async ({ guideId, lang }) => {
  const response = await getGuideDetail({
    guideId,
    languageCode: toLanguageCode(lang),
  });
  return response.data;
};

// data = [{ guideId, guideGuid, code, name, purchaseDate, montoPagado,
//           currencyCode }]
export const getMyPurchasedGuidesService = async ({ lang }) => {
  const response = await getMyPurchasedGuides({ languageCode: toLanguageCode(lang) });
  return response.data;
};

// data = { guideId, guideVariantTypeCode, hasAccess, name, description,
//          content, fileUrl }. Cuando hasAccess viene en false, los demás
//          campos llegan en null y el mensaje del envolvente explica por qué.
export const getGuideContentService = async ({ guideId, variantTypeCode }) => {
  const response = await getGuideContent({
    guideId,
    guideVariantTypeCode: variantTypeCode,
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// VIVA-1214 — cobro de guías.
// ---------------------------------------------------------------------------

// Valor fijo que identifica una compra de Guías en el endpoint genérico.
export const GUIDE_ENTITY_TYPE = 0;

// Un cupón vacío o con solo espacios es "sin cupón": no se manda. El backend
// trata igual "" y null (comprobado en pre), pero así el payload dice lo que es.
const normalizeCoupon = (couponCode) => {
  const code = String(couponCode ?? "").trim();
  return code ? code : null;
};

// TODO el payload de la compra se arma aquí, en un solo lugar: si algún nombre
// de campo cambia en backend, se corrige esta función y nada más.
//
// - El comprador sale del token; email y fullName son datos de contacto que
//   Stripe usa para el recibo, no la identidad de la compra.
// - `expectedAmountUsd` es el total que devolvió precio-preview. El servidor
//   recalcula y responde 409 "El monto no coincide" si difiere: así el usuario
//   nunca paga un número distinto del que vio (comprobado en pre).
export const buildGuidePaymentIntentPayload = ({
  guideId,
  email,
  fullName,
  idempotencyKey,
  couponCode,
  expectedAmountUsd,
}) => {
  const payload = {
    entityType: GUIDE_ENTITY_TYPE,
    guideId,
    email,
    fullName,
    idempotencyKey,
  };

  const coupon = normalizeCoupon(couponCode);
  if (coupon) payload.couponCode = coupon;

  if (Number.isFinite(Number(expectedAmountUsd)) && expectedAmountUsd !== null) {
    payload.expectedAmountUsd = Number(expectedAmountUsd);
  }

  return payload;
};

export const getGuidePricePreviewService = async ({ guideId, couponCode }) => {
  const params = { guideId };
  const coupon = normalizeCoupon(couponCode);
  if (coupon) params.couponCode = coupon;

  const response = await getGuidePricePreview(params);
  return mapGuidePricePreview(response.data?.data);
};

// Forma real de la respuesta, comprobada contra pre el 25/09/2026:
//   data = { status, paymentIntentId, clientSecret, amount, currency }
// `amount` viene en CENTAVOS (499 = 4.99 USD) y `currency` en minúsculas. El
// importe que se muestra sale de precio-preview, que ya lo da en dólares; si
// alguna vez se usa el de aquí, hay que dividirlo entre 100.
//
// EXCEPCIÓN al "sin try/catch" declarado arriba: este catch no vuelve a lanzar
// lo mismo, convierte una respuesta de recuperación en un resultado normal.
//
// Backend responde 409 "Ya tienes un pago en proceso para esta guía" cuando el
// comprador dejó un intento abierto —por ejemplo, cerró el formulario sin
// pagar— y en `data` DEVUELVE ESE INTENTO con su clientSecret para que lo
// termine en vez de crear otro. No es un rechazo: es la forma que tiene el
// servidor de no cobrar dos veces la misma guía. Tratarlo como error dejaba a
// esa persona sin poder comprar esa guía nunca más desde la pantalla, que es
// justo lo que pasaba antes de este cambio.
//
// El otro 409 —"El monto no coincide"— no trae intento y sí es un error: se
// distingue por eso, no por el texto del mensaje, que es del servidor y puede
// cambiar de redacción.
export const createGuidePaymentIntentService = async (input) => {
  try {
    const response = await createGuidePaymentIntent(buildGuidePaymentIntentPayload(input));
    return mapGuidePaymentIntent(response.data?.data);
  } catch (error) {
    if (error?.response?.status !== 409) throw error;

    const enProceso = mapGuidePaymentIntent(error.response.data?.data);
    if (!isPaymentIntentClientSecret(enProceso.clientSecret)) throw error;

    return enProceso;
  }
};

export const getPaymentIntentStatusService = async ({ paymentIntentId }) => {
  const response = await getPaymentIntentStatus(paymentIntentId);
  return mapPaymentIntentStatus(response.data?.data);
};

export const cancelPaymentIntentService = async ({ paymentIntentId }) => {
  const response = await cancelPaymentIntent({ paymentIntentId });
  return response.data;
};

// Sin botón en la interfaz por decisión del 22/09/2026: la política de
// reembolso sigue en revisión legal (VIVA-1220). La capa queda lista para
// exponerla cuando exista.
export const refundGuideService = async ({ guideId, idempotencyKey }) => {
  const response = await refundGuide({ guideId, idempotencyKey });
  return response.data;
};
